"use client";

import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { queryClient } from "@/core/caching";
import { AgelessAssessmentFlowType } from "@orval/model";

import { assessmentSessionStorageService } from "../AssessmentSessionStorageService";
import type { NextStepsPath } from "../components/NextStepsStep/NextStepsStep";

export const getNextStepsPathQueryKey = (
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
) => ["next-steps-path", subdomainSlug, flowType] as const;

type UseNextStepsPathResult = {
  nextStepsPath: NextStepsPath | null;
  getPathSnapshot: () => NextStepsPath | null;
  setPath: (path: NextStepsPath) => void;
  clearPath: () => void;
};

export function useNextStepsPath(
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
): UseNextStepsPathResult {
  const queryKey = getNextStepsPathQueryKey(subdomainSlug, flowType);

  const getPathSnapshot = useCallback(() => {
    const cachedPath = queryClient.getQueryData<NextStepsPath | null>(queryKey);
    if (cachedPath !== undefined && cachedPath !== null) {
      return cachedPath;
    }

    return assessmentSessionStorageService.getNextStepsPath<NextStepsPath>({
      subdomainSlug,
      flowType,
    });
  }, [flowType, queryKey, subdomainSlug]);

  const { data: nextStepsPath = null } = useQuery<NextStepsPath | null>({
    queryKey,
    queryFn: getPathSnapshot,
    initialData: getPathSnapshot,
    staleTime: Infinity,
  });

  const setPath = useCallback(
    (path: NextStepsPath) => {
      assessmentSessionStorageService.setNextStepsPath({ subdomainSlug, flowType }, path);
      queryClient.setQueryData<NextStepsPath | null>(queryKey, path);
    },
    [flowType, queryKey, subdomainSlug],
  );

  const clearPath = useCallback(() => {
    assessmentSessionStorageService.removeNextStepsPath({ subdomainSlug, flowType });
    queryClient.setQueryData<NextStepsPath | null>(queryKey, null);
  }, [flowType, queryKey, subdomainSlug]);

  return { nextStepsPath, getPathSnapshot, setPath, clearPath };
}

export const clearNextStepsPath = async (
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
) => {
  assessmentSessionStorageService.removeNextStepsPath({ subdomainSlug, flowType });
  await queryClient.removeQueries({
    queryKey: getNextStepsPathQueryKey(subdomainSlug, flowType),
  });
};
